import { TaskType } from '@/shared/domain/taskTypes';
import { isAgenticUsecase } from './taskTypeIcons';

/**
 * Agentic scope driven by the "Agentic" toggle in the Projects filter bar.
 * `all` (default, toggle off) shows every task; `agentic` (toggle on) keeps
 * only tasks with tool calling and badges the All Task Types option tiles.
 */
export const AGENTIC_FILTER_ALL = 'all';
export const AGENTIC_FILTER_AGENTIC = 'agentic';

/** Coerce a stored / unknown value (e.g. a stale sessionStorage entry) to a valid mode. */
export const normalizeAgenticFilter = (mode) => (
    mode === AGENTIC_FILTER_AGENTIC ? AGENTIC_FILTER_AGENTIC : AGENTIC_FILTER_ALL
);

/** True while the Agentic toggle is on. */
export const isAgenticOnlyFilter = (mode) => normalizeAgenticFilter(mode) === AGENTIC_FILTER_AGENTIC;

/** True when `usecase` passes the agentic scope (everything while the toggle is off). */
export const matchesAgenticFilter = (usecase, mode) => (
    !isAgenticOnlyFilter(mode) || isAgenticUsecase(usecase)
);

/** Filter values for single- vs multi-label classification. `task_type` is the same for both. */
export const CLASSIFICATION_SINGLE_FILTER = `${TaskType.classification}:single`;
export const CLASSIFICATION_MULTI_FILTER = `${TaskType.classification}:multi`;

/** `iconType` is the Spark `TaskTypeIcon` tile shown next to each option. */
export const TASK_TYPE_FILTER_OPTIONS = [
    { value: CLASSIFICATION_SINGLE_FILTER, label: 'Single label classification', iconType: 'classification' },
    { value: CLASSIFICATION_MULTI_FILTER, label: 'Multi label classification', iconType: 'classification-multilabel' },
    { value: TaskType.NER, label: 'Entity extraction', iconType: 'extraction' },
    { value: TaskType.summarization, label: 'Summarization', iconType: 'summarization' },
    { value: TaskType.contentGeneration, label: 'Content Generation', iconType: 'content-generation' },
];

/**
 * Expand a stored filter so a legacy `ClassificationResponse` value (no
 * single/multi suffix) selects both classification options in the dropdown.
 */
export const expandTaskTypeFilterValues = (filter) => {
    if (!filter || filter === 'All') return [];
    const selected = Array.isArray(filter) ? filter : [filter];
    return selected.flatMap((value) => (
        value === TaskType.classification
            ? [CLASSIFICATION_SINGLE_FILTER, CLASSIFICATION_MULTI_FILTER]
            : [value]
    ));
};

const itemMatchesFilterValue = (item, value) => {
    if (value === CLASSIFICATION_SINGLE_FILTER) {
        return item?.task_type === TaskType.classification && item?.is_multilabel !== true;
    }
    if (value === CLASSIFICATION_MULTI_FILTER) {
        return item?.task_type === TaskType.classification && item?.is_multilabel === true;
    }
    if (value === TaskType.classification) {
        return item?.task_type === TaskType.classification;
    }
    return item?.task_type === value;
};

/** True when `item` (usecase or training) matches the All Task Types filter. */
export const matchesTaskTypeFilter = (item, filter) => {
    if (!filter || filter === 'All') return true;
    const selected = Array.isArray(filter) ? filter : [filter];
    return selected.some((value) => itemMatchesFilterValue(item, value));
};
